/* eslint-disable @typescript-eslint/no-non-null-assertion */
import moment from 'moment';

import {
  OdProtosListingsData_Listing_State as LISTING_STATE,
  OdProtosListingsData_LwcProduct_State as LWC_PRODUCT_STATE,
  OdProtosAgentsData_AgentLead_State,
  OdProtosWebData_ORVAState,
} from '../__generated__/athenaGlobalTypes';
import { GetExteriorAssessmentData } from '../graphql/athena/__generated__/GetExteriorAssessmentData';
import { LeadDetails } from '../graphql/athena/queries/__generated__/LeadDetails';
import { LwcDetails_odProtosListingsServicesListwithcertaintyproduct_getLwcDetails_lwcDetails as LwcDetails } from '../graphql/athena/queries/__generated__/LwcDetails';
import { LwcDetailsForBrokerage_odProtosListingsServicesListwithcertaintyproduct_getLwcDetailsForBrokerage_lwcDetails as LwcBrokerageDetails } from '../graphql/athena/queries/__generated__/LwcDetailsForBrokerage';
import {
  centsToCurrencyString,
  formatPercentage,
  formatPhoneNumberInput,
  isNullDateString,
} from '../helpers/formatUtils';

export const NULL_FIELD_DISPLAY = '-';

export interface lwcProductDataProps {
  token: string;
  lwcProductState: LWC_PRODUCT_STATE;
  listingState: LISTING_STATE; //
  lwcProductId: string;
  agentLeadId: string;
  agentLeadState: OdProtosAgentsData_AgentLead_State;
  assignedBrokerageId: string;
  listingId: string;
  listingPrice?: number | null;
  soldPrice?: number | null;
  grossCommission?: number | null;
  presentationType?: string | null;
  agentNote?: string | null;
  cepNote?: string | null;
  leadPassBackNote?: string | null;
  lwcProductTimestampsMap: Map<string, string>;
  listingStateEntryTimes: Map<LISTING_STATE, string>;
  // Whether the AgentLead associated with this LwcProduct is pending either brokerage or agent acceptance
  pendingAssignmentAcceptance: boolean;
  isLatestLwcLead: boolean;
}
/** Customer data, usually we will have a customer's name address and email but there isn't always a phone number since we don't normally collect that in our flow outside of ORVA scheduling */
export interface customerDataProps {
  name: string;
  phone?: string | null;
  email?: string | null;
  address: string;
}

/** This encompasses the preliminary offer data as well as the final offer data. The preliminary offer is where our estimated listing range comes from and will not be updated once the final offer is ready. Keep in mind that our listing range is Opendoor's best estimate while the agent listing price is a completely separate value that is discussed by the agent and the customer */
export interface offerDataProps {
  state?: string;
  headlinePrice?: string;
  listingEstimateCeiling?: string;
  listingEstimateFloor?: string;
  expiresAt: string;
  initialInspectionAt?: Date;
  finalOfferData?: finalOfferDataProps;
}
/** Final offer data will be populated only once our offer state moves into the WAITING_FOR_LIST_WITH_CERTAINTY state in web's offer state machine. This state is only entered for homes in list with certainty that have had underwriting completed. Once that state is entered it is a pretty safe bet that the rest of this data will be populated as well */
export interface finalOfferDataProps {
  state?: string;
  finalOfferPrice?: string | null;
  finalOfferFeePercentage?: string | null;
  finalizedFeeCost?: string | null;
  repairCosts?: string | null;
  estimatedClosingCosts?: string | null;
  finalizedHeadlinePrice?: string | null;
  estimatedNetProceeds?: string | null;
  finalOfferViewedAt?: string | null;
}

/** Address data, market might be useful for determining features that the customer has access to as well as for double checking that the percentage is correct for customers since some markets have 6% fees instead of 5%. */
export interface addressDataProps {
  street?: string;
  city?: string;
  state?: string;
  market?: string | null;
  postalCode?: string;
  unitNumber?: string | null;
  timeZone?: string | null;
}

// Open to suggestions on how to better name this
export interface LeadObject {
  [key: string]: SingleLead;
}

/** The customer facing experience partner. Most likely the person that interacted with the customer during their Virtual Assessment call */
export interface experiencePartnerDataProps {
  name?: string;
  email?: string;
}

export interface homeAssessmentData {
  currentInspectionTime?: Date;
}

/** The shaped data containing the cumulative data for a lead */
export interface SingleLead {
  lwcProductData: lwcProductDataProps;
  experiencePartner?: experiencePartnerDataProps;
  addressData: addressDataProps;
  customerData: customerDataProps;
  offerData: offerDataProps;
  homeAssessmentData?: homeAssessmentData;
}

const PT_TIMEZONE = 'America/Los_Angeles';

export function convertLeadDetails(
  data: LeadDetails,
  lwcDetails: LwcDetails | LwcBrokerageDetails,
  exteriorAssessmentData?: GetExteriorAssessmentData | undefined,
): SingleLead {
  const sellDirectDetails =
    data?.odProtosListingsServicesListwithcertaintyproduct?.getSellDirectDetails;
  const offer = sellDirectDetails?.lead?.latestOffer;
  const lead = sellDirectDetails?.lead;

  const finalOfferData = {
    state: offer?.underwriting?.state,
    finalOfferPrice: centsToCurrencyString(offer?.underwriting?.finalizedHeadlinePriceCents ?? 0),
    finalOfferFeePercentage: formatPercentage(offer?.underwriting?.finalizedFeePercentage ?? 0),
    finalizedFeeCost: centsToCurrencyString(offer?.underwriting?.finalizedFeeCostCents ?? 0),
    estimatedNetProceeds: centsToCurrencyString(
      offer?.underwriting?.estimatedNetProceedsCents ?? 0,
    ),
    estimatedClosingCosts: centsToCurrencyString(
      offer?.underwriting?.estimatedClosingCostsCents ?? 0,
    ),
    repairCosts: centsToCurrencyString(offer?.underwriting?.repairAddendumAdjustmentCents ?? 0),
    finalOfferViewedAt: offer?.finalOfferViewedAt
      ? new Date(offer?.finalOfferViewedAt).toLocaleDateString()
      : undefined,
  };
  const formattedExpirationDate =
    offer?.state === 'EXPIRED'
      ? 'Expired'
      : moment(offer?.expirationInfo?.date).tz(PT_TIMEZONE).format('MM/DD/YYYY');

  const offerData = {
    state: offer?.state,
    headlinePrice: centsToCurrencyString(offer?.headlinePriceCents ?? 0),
    listingEstimateCeiling: centsToCurrencyString(offer?.listingEstimateCeilingCents ?? 0),
    listingEstimateFloor: centsToCurrencyString(offer?.listingEstimateFloorCents ?? 0),
    expiresAt: formattedExpirationDate,
    initialInspectionAt: offer?.initialInspectionAt
      ? new Date(offer?.initialInspectionAt)
      : undefined,
    finalOfferData,
  };

  // Offer data end
  // Address data start
  const address = lead?.address;
  const addressData = {
    street: address?.street,
    city: address?.city,
    state: address?.state,
    market: address?.market,
    postalCode: address?.postalCode,
    unitNumber: address?.unitNumber,
    timeZone: address?.timeZone,
  };
  // Address data end
  // Customer data start
  const customer = lead?.customerV2;
  const customerAddress = address?.unitNumber
    ? `${address?.street}, Unit ${address?.unitNumber}, ${address?.city}, ${address?.state}, ${address?.postalCode}`
    : `${address?.street}, ${address?.city}, ${address?.state}, ${address?.postalCode}`;
  const customerData = {
    name: `${customer?.firstName} ${customer?.lastName}`,
    phone: formatPhoneNumberInput(customer?.primaryPhoneNumber?.number?.value ?? ''),
    email: customer?.primaryEmail?.address?.value || customer?.otherEmails?.[0]?.address?.value,
    address: customerAddress,
  };
  // Customer data end
  // Experience partner data start
  const experiencePartnerData = {
    name: lead?.experiencePartner?.name,
    email: lead?.experiencePartner?.email,
  };

  const lwcProductData = buildLwcProductData(lwcDetails);

  const currentInspectionTime =
    exteriorAssessmentData?.web.getAllExteriorAssessmentSlots?.currentInspectionTime;
  const homeAssessmentData = {
    currentInspectionTime: currentInspectionTime ? new Date(currentInspectionTime) : undefined,
  };

  // End of shaping data
  return {
    lwcProductData,
    experiencePartner: experiencePartnerData,
    customerData,
    offerData,
    addressData,
    homeAssessmentData,
  };
}

// returns local date formatted like 6/22/2023
export const getLocalReadableDate = (date: string): string => {
  return !isNullDateString(date)
    ? `${moment.utc(date).local().format('M/D/YYYY')}`
    : NULL_FIELD_DISPLAY;
};

export const getOrvaCompletedAtDateString = (leadDetails: LeadDetails) => {
  let orvaCompletedAtStr = NULL_FIELD_DISPLAY;

  const sellDirectDetails =
    leadDetails?.odProtosListingsServicesListwithcertaintyproduct?.getSellDirectDetails;
  const lead = sellDirectDetails?.lead;

  if (lead) {
    const orvaState = lead.latestVirtualAssessment?.orva?.state;

    if (
      orvaState === OdProtosWebData_ORVAState.ORVA_COMPLETED &&
      lead.lastOfferReviewDetails?.offerReviewAt
    ) {
      orvaCompletedAtStr = getLocalReadableDate(lead.lastOfferReviewDetails.offerReviewAt);
    }
  }
  return orvaCompletedAtStr;
};

export const getReadableAddressFromSingleLead = (lead: SingleLead) => {
  if (lead.addressData.unitNumber) {
    return `${lead.addressData.street}, Unit ${lead.addressData.unitNumber}, ${lead.addressData.city}, ${lead.addressData.state}, ${lead.addressData.postalCode}`;
  }
  return `${lead.addressData.street}, ${lead.addressData.city}, ${lead.addressData.state}, ${lead.addressData.postalCode}`;
};

export function buildLwcProductData(lwcDetails: LwcDetails | LwcBrokerageDetails) {
  const lwcProduct = lwcDetails.lwcProduct!;
  const listing = lwcDetails.listing!;
  const agentLead = lwcDetails.agentLead!;

  //convert lwcProduct.timestamps to timestamps map
  const lwcProductTimestampsMap = new Map<string, string>();
  lwcProduct.timestamps.forEach((timestamp) => {
    const dateOnlyTimestamp = new Date(timestamp.timestamp).toISOString().split('T')[0];
    lwcProductTimestampsMap.set(timestamp.key, dateOnlyTimestamp);
  });

  const listingStateEntryTimes = new Map<LISTING_STATE, string>();
  listing.timestamps.forEach((timestamp) => {
    const dateOnlyTimestamp = new Date(timestamp.timestamp).toISOString().split('T')[0];
    listingStateEntryTimes.set(timestamp.state, dateOnlyTimestamp);
  });

  const pendingAssignmentAcceptance =
    agentLead.state === OdProtosAgentsData_AgentLead_State.BROKERAGE_PROPOSED ||
    agentLead.state === OdProtosAgentsData_AgentLead_State.AGENTS_PROPOSED;

  const isLatestLwcLead = listing.id === lwcProduct.listingId;

  // Experience partner data end
  // LWC product data start
  const lwcProductData = {
    token: lwcProduct.sellerLeadToken,
    lwcProductState: lwcProduct.state,
    listingState: listing.state,
    lwcProductId: lwcProduct.id,
    agentLeadId: agentLead.id,
    agentLeadState: agentLead.state,
    assignedBrokerageId: agentLead?.assignedBrokerageId,
    listingId: listing?.id,
    listingPrice: listing?.listingPrice ?? 0,
    soldPrice: listing?.soldPrice ?? 0,
    grossCommission: listing?.grossCommission ?? 0,
    presentationType: lwcProduct.presentationType,
    agentNote: lwcProduct.agentNote,
    cepNote: lwcProduct.cepNote,
    leadPassBackNote: lwcProduct.leadPassBackNote,
    lwcProductTimestampsMap,
    listingStateEntryTimes,
    pendingAssignmentAcceptance,
    isLatestLwcLead,
  };
  return lwcProductData;
}

export const LWC_PRODUCT_SORT_ORDER: LWC_PRODUCT_STATE[] = [
  LWC_PRODUCT_STATE.UNKNOWN,
  LWC_PRODUCT_STATE.CREATED,
  LWC_PRODUCT_STATE.LISTING_INITIATED,
  LWC_PRODUCT_STATE.FIRST_CONTACT_ATTEMPTED,
  LWC_PRODUCT_STATE.LISTING_PRESENTATION_SCHEDULED,
  LWC_PRODUCT_STATE.LISTING_PRESENTATION_COMPLETE,
  LWC_PRODUCT_STATE.CONVERTED_LISTING, //Pending deprecation after BE backfill
  LWC_PRODUCT_STATE.SELECTED_LISTING,
  LWC_PRODUCT_STATE.SOLD_ON_MARKET,
  LWC_PRODUCT_STATE.CONVERTED_DIRECT_SALE, //Pending deprecation after BE backfill
  LWC_PRODUCT_STATE.SELECTED_DIRECT_SALE,
  LWC_PRODUCT_STATE.DNC_DIRECT_SALE_ONLY,
  LWC_PRODUCT_STATE.DNC_COMPETITOR,
  LWC_PRODUCT_STATE.DNC_NON_RESPONSIVE,
  LWC_PRODUCT_STATE.DNC_NOT_BUYING_SELLING,
  LWC_PRODUCT_STATE.DNC_NOT_INTERESTED,
  LWC_PRODUCT_STATE.NOT_ELIGIBLE,
  LWC_PRODUCT_STATE.DELETED,
];

export const LISTING_SORT_ORDER: LISTING_STATE[] = [
  LISTING_STATE.UNKNOWN,
  LISTING_STATE.AGENT_ASSIGNMENT,
  LISTING_STATE.OWNER_LIST_DECISION,
  LISTING_STATE.LISTING_AGREEMENT_SIGNED,
  LISTING_STATE.LISTING_ACTIVE,
  LISTING_STATE.IN_CONTRACT,
  LISTING_STATE.SOLD,
  LISTING_STATE.LEAD_TERMINATED,
  LISTING_STATE.LISTING_TERMINATED,
  LISTING_STATE.NOT_LISTED,
];

export const SELECTED_LISTING_LWC_PRODUCT_STATES = [
  LWC_PRODUCT_STATE.CONVERTED_LISTING, //Pending deprecation after BE backfill
  LWC_PRODUCT_STATE.SELECTED_LISTING,
];

// The nuance of opting into direct sale before/after listing agreement signing is
// not exposed to the user.
// The user sees both options as "Convert to sell direct" in the UI, and have the option
// to update the notes in both cases.
export const OPTED_FOR_DIRECT_SALE_LWC_PRODUCT_STATES = [
  LWC_PRODUCT_STATE.DNC_DIRECT_SALE_ONLY,
  LWC_PRODUCT_STATE.CONVERTED_DIRECT_SALE, //Pending deprecation after BE backfill
  LWC_PRODUCT_STATE.SELECTED_DIRECT_SALE,
];

export const compareLwcDetails = (
  a: LwcDetails | LwcBrokerageDetails,
  b: LwcDetails | LwcBrokerageDetails,
) => {
  const result =
    LWC_PRODUCT_SORT_ORDER.indexOf(a.lwcProduct!.state) -
    LWC_PRODUCT_SORT_ORDER.indexOf(b.lwcProduct!.state);

  if (result === 0 && SELECTED_LISTING_LWC_PRODUCT_STATES.includes(a.lwcProduct!.state)) {
    return (
      LISTING_SORT_ORDER.indexOf(a.listing!.state) - LISTING_SORT_ORDER.indexOf(b.listing!.state)
    );
  }
  return result;
};
